import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

export type InvoiceStatus = 'PENDING' | 'REVIEWED';

export interface InvoiceLineItem {
  vehicleID: string;
  platNomor: string;
  hubName: string | null;
  modelName: string;
  contractStartDate: string | null;
  returnDate: string | null;
  activeDays: number;
  dailyRate: number;
  subtotal: number;
  flagReason: 'missing_contract_start' | 'zero_daily_rate' | null;
  isFlagged: boolean;
}

export interface InvoiceGroupedSummaryRow {
  modelName: string;
  activeDays: number;
  vehicleCount: number;
  dailyRate: number;
  subtotal: number;
}

export interface InvoiceResponse {
  oem: { id: string; name: string };
  period: string;
  lineItems: InvoiceLineItem[];
  flaggedCount: number;
  grandTotal: number;
  grandTotalWithVat: number;
  groupedSummary: InvoiceGroupedSummaryRow[];
  status: InvoiceStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewedTotal: number | null;
  dataChangedDelta: number | null;
  isPeriodInProgress: boolean;
}

export interface MarkReviewedRequest {
  oemId: string;
  period: string;
  updatedBy: string;
}

export interface MarkReviewedResponse {
  status: InvoiceStatus;
  reviewedBy: string;
  reviewedAt: string;
  reviewedTotal: number;
}

export const getInvoice = async (params: {
  oem_id: string;
  period: string;
}): Promise<InvoiceResponse> => {
  try {
    const response = await Axios.get('/fleet/v1/invoice-recon', { params });
    return response.data.data;
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const markInvoiceReviewed = async (
  data: MarkReviewedRequest
): Promise<MarkReviewedResponse> => {
  try {
    const response = await Axios.patch('/fleet/v1/invoice-recon/status', data);
    return response.data.data;
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const downloadInvoiceExport = async (params: {
  oem_id: string;
  period: string;
  format?: 'csv';
}): Promise<Blob> => {
  try {
    const response = await Axios.get('/fleet/v1/invoice-recon/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
